// The most perfect location pin icon you ever saw.
//  Coded at 2am like all good things.
//
import { LocationOnTwoTone } from '@mui/icons-material';
import { Avatar, Badge, Box, IconButton, Paper, Popper, Typography } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';

import { AXLE_TEAL, TEXT_GRAY } from '../../../../constants/colors';
import { QhpWithLocationInfo } from '../../../../fetching/api/liveLocations';
import { ClickHandler } from '../../../../utils/clickEvent';
import { formatInitials } from '../../../../utils/formatting';
import { NewTabLink } from '../../../../utils/links';
import { Component, ReturnComponent } from '../../../../utils/reactTypes';

// Hover-intent debounce for the pin popover (ms): a short delay before opening so
// dragging the cursor across dense pins doesn't flash a popover on each, and a short
// delay before closing so the cursor can travel from the pin into the popover.
const HOVER_OPEN_DELAY_MS = 120;
const HOVER_CLOSE_DELAY_MS = 120;

export type CustomPinIcon = ({
    onClick,
    color,
}: {
    onClick?: ClickHandler;
    color?: string;
}) => ReturnComponent;

const LocationPinIcon = ({
    color,
    badgeContent,
    onClick,
}: {
    color: string;
    badgeContent?: Component | undefined;
    onClick?: ClickHandler;
}) => {
    return (
        <IconButton
            onClick={onClick}
            aria-label='Location pin'
            sx={{
                // PERFECTLY place the icon on top of the route.
                transform: 'translate(-50%, -75%)',

                // Now let's give these icons some CHONK.
                //  Yes, we're going to directly style the SVG drawing inside the <LocationOnTwoTone/> icon.
                //  Otherwise, the pins kinda blend into each other and with the route.

                // Outer outline.
                '& svg path:nth-of-type(2)': {
                    fill: 'black',
                },
                // Inner circle.
                '& svg circle': {
                    fill: 'black',
                },
                // Fill in the middle.
                '& svg path:nth-of-type(1)': {
                    // I initially tried using the `color` CSS property, but while it works in
                    //  Chrome, it doesn’t work in Safari.
                    fill: color,
                    opacity: 0.9, // The default opacity is 0.3, which is too low.
                },
            }}
        >
            <Badge
                // If `badgeContent` is falsy, the badge will not show.
                badgeContent={badgeContent}
                sx={{
                    '& .MuiBadge-badge': {
                        // Reset the default styles.
                        padding: 0,
                        minWidth: 'unset',
                        minHeight: 'unset',
                        // Configure our own size.
                        height: '18px',
                        width: '18px',
                        fontSize: '14px !important',
                        // Positioning it correctly.
                        transform: 'translate(0px, -7.5px)',
                        // Coloring it correctly.
                        color: 'white',
                        backgroundColor: '#333',
                    },
                }}
            >
                <LocationOnTwoTone
                    style={{
                        // Ahhhhh yes, gimme that chonk.
                        fontSize: '35px',
                    }}
                />
            </Badge>
        </IconButton>
    );
};

// We memoize this component, as it was being rendered 5 times per page load,
//  Multiply that by 100, for one pin each for 100 visits, and it slowed down
//  the page render a lot.
export const GoogleMapPin = memo(
    ({
        color,
        badgeContent,
        popoverComponent = null,
        onClick,
        CustomPinIcon,
    }: {
        color: string;
        badgeContent?: Component | undefined;
        popoverComponent?: Component | undefined;
        onClick?: ClickHandler;
        CustomPinIcon?: CustomPinIcon;
    }) => {
        //

        const hasPopover = !!popoverComponent;

        // Hover-to-open: the popover opens while the cursor rests on the pin and
        // closes when it leaves. Because the pin and the popover share the same
        // open/close handlers, the cursor can travel from one to the other (e.g. to
        // click a link inside the popover) without it closing. `onClick` is still
        // passed straight through for navigation / spiderfy expansion.
        const anchorRef = useRef<HTMLElement>(null);
        const [isOpen, setIsOpen] = useState(false);
        const openTimer = useRef<ReturnType<typeof setTimeout>>();
        const closeTimer = useRef<ReturnType<typeof setTimeout>>();

        const handleOpen = () => {
            if (!hasPopover) {
                return;
            }
            if (closeTimer.current) {
                clearTimeout(closeTimer.current);
            }
            if (isOpen) {
                return;
            }
            openTimer.current = setTimeout(() => setIsOpen(true), HOVER_OPEN_DELAY_MS);
        };
        const handleClose = () => {
            if (openTimer.current) {
                clearTimeout(openTimer.current);
            }
            closeTimer.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
        };

        // Clear any pending timers on unmount.
        useEffect(
            () => () => {
                if (openTimer.current) {
                    clearTimeout(openTimer.current);
                }
                if (closeTimer.current) {
                    clearTimeout(closeTimer.current);
                }
            },
            [],
        );

        // If the popover is removed (e.g. a spiderfy cluster collapses), hide it.
        useEffect(() => {
            if (!hasPopover) {
                setIsOpen(false);
            }
        }, [hasPopover]);

        return (
            <>
                <Box
                    ref={anchorRef}
                    component='span'
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                    sx={{ display: 'inline-flex' }}
                >
                    {!CustomPinIcon ? (
                        <LocationPinIcon
                            color={color}
                            badgeContent={badgeContent}
                            onClick={onClick}
                        />
                    ) : (
                        <CustomPinIcon onClick={onClick} color={color} />
                    )}
                </Box>

                {hasPopover ? (
                    <Popper
                        open={isOpen}
                        anchorEl={anchorRef.current}
                        placement='right-start'
                        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
                    >
                        <Paper elevation={3} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
                            <Box padding={2}>{popoverComponent}</Box>
                        </Paper>
                    </Popper>
                ) : null}
            </>
        );
    },
);

GoogleMapPin.displayName = 'GoogleMapPin';

export const getBadgeContentFromStopIndex = (stopIndex: number | null): Component | undefined =>
    stopIndex === null ? undefined : <>{stopIndex + 1}</>;

export const iconForQhpWithLocationInfo = (
    qhpWithLocationInfo: QhpWithLocationInfo,
): CustomPinIcon => {
    const component = ({ onClick }: { onClick?: ClickHandler }) => (
        <Box
            sx={{
                // Control the size.
                height: '25px',
                width: '25px',

                // PERFECTLY place the icon on top of the route.
                transform: 'translate(-50%, -75%)',

                // Help position the pulsing animation.
                position: 'relative',
            }}
        >
            {/* Pulsing rings for the "live location" effect. */}
            {/* This needs to come before the <Avatar/> component, so it goes under it. */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '25px',
                    height: '25px',
                    borderRadius: '50%',
                    border: `2px solid ${AXLE_TEAL}`,
                    animation: 'pulse 3s infinite',
                    '@keyframes pulse': {
                        '0%': {
                            width: '25px',
                            height: '25px',
                            opacity: 0.8,
                        },
                        '50%': {
                            width: '40px',
                            height: '40px',
                            opacity: 0,
                        },
                        '100%': {
                            width: '25px',
                            height: '25px',
                            opacity: 0,
                        },
                    },
                }}
            />
            <Avatar
                onClick={onClick}
                src={qhpWithLocationInfo.qhp.logo_url ?? undefined}
                alt={qhpWithLocationInfo.qhp.name}
                sx={{
                    // Need to repeat the size yet again here.
                    height: '25px',
                    width: '25px',

                    // Text formatting (needs to be specified here).
                    fontSize: '1em',
                    bgcolor: TEXT_GRAY,

                    // Make it look clickable.
                    cursor: 'pointer',
                }}
            >
                {formatInitials(
                    qhpWithLocationInfo.qhp.first_name,
                    qhpWithLocationInfo.qhp.last_name,
                )}
            </Avatar>
        </Box>
    );
    component.displayName = 'IconForQhpWithLocationInfo';
    return component;
};

export const popoverForQhpWithLocationInfo = (
    qhpWithLocationInfo: QhpWithLocationInfo,
): ReturnComponent => {
    return (
        <Typography>
            <NewTabLink to={`/clinicians/${qhpWithLocationInfo.qhp.uuid}`}>
                {qhpWithLocationInfo.qhp.name}
            </NewTabLink>{' '}
            last updated their location at {qhpWithLocationInfo.location_info.timestamp.fromNow()}.
        </Typography>
    );
};
